/* ---------------------------------------------------------------------------
 * Lande Paris — comportements de marque portés du prototype vers Horizon.
 * Vanilla, sans dépendance, chargé en `defer` (script classique, pas module).
 *
 * 1. Les écouteurs globaux sont posés UNE fois et tout passe par délégation
 *    sur `document` : le markup peut être remplacé sans rien rebrancher.
 * 2. `Lande.init()` est idempotent. Les éléments à état sont marqués dans un
 *    WeakSet — un nœud remplacé n'y figure plus, il est donc réinitialisé.
 * 3. Le JS ne pose que des attributs `data-lande-*` ; le CSS fait le reste.
 * ------------------------------------------------------------------------- */

(function () {
  'use strict';

  // Le script chargé deux fois ne réinstalle rien : il se contente de rescanner.
  if (window.Lande && typeof window.Lande.init === 'function') {
    window.Lande.init();
    return;
  }

  const FOCUSABLE =
    'a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),' +
    'select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"]),' +
    'summary,audio[controls],video[controls]';

  // En-tête : marge de sécurité près du sommet, et seuil sous lequel on
  // considère qu'un delta de scroll est du bruit de trackpad.
  const HEADER_GUARD_PX = 120;
  const HEADER_NOISE_PX = 6;

  const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const reducedMotion = () => motionQuery.matches;

  const esc = (value) => (window.CSS && CSS.escape ? CSS.escape(value) : value);
  const el = (selector, root) => (root || document).querySelector(selector);
  const all = (selector, root) => Array.from((root || document).querySelectorAll(selector));

  /* --- Verrou de défilement ---------------------------------------------- */

  let savedOverflow = null;
  function lockScroll(on) {
    const root = document.documentElement;
    if (on) {
      if (savedOverflow === null) savedOverflow = document.body.style.overflow;
      root.setAttribute('data-lande-scroll-lock', 'true');
      document.body.style.overflow = 'hidden';
      return;
    }
    root.removeAttribute('data-lande-scroll-lock');
    document.body.style.overflow = savedOverflow === null ? '' : savedOverflow;
    savedOverflow = null;
  }

  /* --- Panneaux latéraux -------------------------------------------------
   * Un panneau porte `data-lande-panel="<nom>"`, ses déclencheurs
   * `data-lande-panel-open="<nom>"`. Overlay partagé, focus trap, Échap,
   * restitution du focus, navigation à deux niveaux optionnelle.
   * --------------------------------------------------------------------- */

  let activePanel = null;
  let focusBeforePanel = null;

  const panelByName = (name) => el('[data-lande-panel="' + esc(name) + '"]');

  function focusablesIn(root) {
    // `offsetParent` écarte ce qui est masqué : vues repliées, éléments hidden.
    return all(FOCUSABLE, root).filter((node) => node.offsetParent !== null || node === document.activeElement);
  }

  function setOverlay(open) {
    all('[data-lande-overlay]').forEach((node) => {
      node.setAttribute('data-open', String(open));
      node.setAttribute('aria-hidden', String(!open));
    });
  }
  // Navigation à deux niveaux. Sans `[data-lande-view]` dans le panneau,
  // l'appel ne fait rien : le panneau simple reste un panneau simple.
  function showView(panel, name) {
    const views = all('[data-lande-view]', panel);
    if (views.length === 0) return;
    views.forEach((view) => {
      const active = view.getAttribute('data-lande-view') === name;
      view.hidden = !active;
      view.setAttribute('data-lande-view-state', active ? 'in' : 'out');
      if (active) view.scrollTop = 0;
    });
  }

  const currentView = (panel) => all('[data-lande-view]', panel).find((view) => !view.hidden) || null;

  function openPanel(name) {
    const panel = panelByName(name);
    if (!panel || panel === activePanel) return;
    if (activePanel) closePanel(activePanel, { restoreFocus: false });
    focusBeforePanel = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    activePanel = panel;
    panel.removeAttribute('inert');
    panel.setAttribute('aria-hidden', 'false');
    panel.setAttribute('data-open', 'true');
    all('[data-lande-panel-open="' + esc(name) + '"]').forEach((btn) => btn.setAttribute('aria-expanded', 'true'));
    setOverlay(true);
    lockScroll(true);
    showView(panel, 'root');
    const target = el('[data-lande-panel-close]', panel) || focusablesIn(panel)[0];
    if (target) target.focus({ preventScroll: true });
  }

  function closePanel(panel, options) {
    if (!panel) return;
    const restoreFocus = !options || options.restoreFocus !== false;
    const name = panel.getAttribute('data-lande-panel');
    panel.setAttribute('data-open', 'false');
    if (name) {
      all('[data-lande-panel-open="' + esc(name) + '"]').forEach((btn) => btn.setAttribute('aria-expanded', 'false'));
    }

    // On rend le focus AVANT de neutraliser le panneau : masquer un conteneur
    // qui détient encore le focus est une faute d'accessibilité.
    if (restoreFocus && focusBeforePanel && document.contains(focusBeforePanel)) {
      focusBeforePanel.focus({ preventScroll: true });
    } else if (panel.contains(document.activeElement) && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    panel.setAttribute('aria-hidden', 'true');
    panel.setAttribute('inert', '');

    if (activePanel === panel) {
      activePanel = null;
      focusBeforePanel = null;
      setOverlay(false);
      lockScroll(false);
    }
  }

  // Tab et Maj+Tab bouclent à l'intérieur du panneau ouvert.
  function trapTab(event) {
    if (!activePanel) return;
    const items = focusablesIn(activePanel);
    if (items.length === 0) {
      event.preventDefault();
      activePanel.focus({ preventScroll: true });
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    const current = document.activeElement;
    if (!activePanel.contains(current)) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus({ preventScroll: true });
      return;
    }
    if (event.shiftKey && current === first) {
      event.preventDefault();
      last.focus({ preventScroll: true });
      return;
    }
    if (!event.shiftKey && current === last) {
      event.preventDefault();
      first.focus({ preventScroll: true });
    }
  }

  // État de repos d'un panneau non encore ouvert : fermé, neutralisé.
  const panelsSeen = new WeakSet();
  function scanPanels() {
    all('[data-lande-panel]').forEach((panel) => {
      if (panelsSeen.has(panel)) return;
      panelsSeen.add(panel);
      if (panel === activePanel) return;
      if (!panel.hasAttribute('role')) panel.setAttribute('role', 'dialog');
      panel.setAttribute('aria-modal', 'true');
      panel.setAttribute('data-open', 'false');
      panel.setAttribute('aria-hidden', 'true');
      panel.setAttribute('inert', '');
      showView(panel, 'root');
    });
    if (!activePanel) setOverlay(false);
  }

  /* --- Galerie produit ---------------------------------------------------
   * Piste en `scroll-snap`, flèches précédent/suivant. L'index n'est pas tenu
   * en mémoire : il se déduit de `scrollLeft`, ce qui reste juste après un
   * défilement tactile, un redimensionnement ou un remplacement de DOM.
   * ------------------------------------------------------------------- */

  const galleriesSeen = new WeakSet();
  const trackOf = (gallery) => el('[data-lande-gallery-track]', gallery);
  const slideCount = (track) => track.children.length;
  function slideIndex(track) {
    const width = track.clientWidth;
    if (width <= 0) return 0;
    return Math.max(0, Math.min(slideCount(track) - 1, Math.round(track.scrollLeft / width)));
  }

  function syncGallery(gallery, forcedIndex) {
    const track = trackOf(gallery);
    if (!track) return;
    const count = slideCount(track);
    const many = count > 1;
    const prev = el('[data-lande-gallery-prev]', gallery);
    const next = el('[data-lande-gallery-next]', gallery);
    // Une seule photo : on masque les flèches sans les retirer du DOM, pour
    // ne pas casser un re-rendu de section qui les réinjecterait.
    if (prev) prev.hidden = !many;
    if (next) next.hidden = !many;
    gallery.setAttribute('data-lande-gallery-multiple', String(many));
    if (!many) return;

    // Pendant un défilement doux, on affiche déjà l'état de destination.
    const index = typeof forcedIndex === 'number' ? forcedIndex : slideIndex(track);
    if (prev) prev.disabled = index <= 0;
    if (next) next.disabled = index >= count - 1;
    gallery.setAttribute('data-lande-index', String(index));
    const status = el('[data-lande-gallery-status]', gallery);
    if (status) {
      // Comparaison avant écriture : réécrire à l'identique déclencherait le
      // MutationObserver et ferait tourner le scan en boucle.
      const label = index + 1 + ' / ' + count;
      if (status.textContent !== label) status.textContent = label;
    }
  }

  // Base du pas suivant : l'index affiché connaît la destination d'un
  // défilement doux encore en cours, là où `scrollLeft` accuse un retard.
  function shownIndex(gallery, track) {
    const shown = Number(gallery.getAttribute('data-lande-index'));
    return Number.isInteger(shown) && shown >= 0 && shown < slideCount(track) ? shown : slideIndex(track);
  }

  function goToSlide(gallery, index) {
    const track = trackOf(gallery);
    if (!track) return;
    const target = Math.max(0, Math.min(slideCount(track) - 1, index));
    track.scrollTo({ left: target * track.clientWidth, behavior: reducedMotion() ? 'auto' : 'smooth' });
    syncGallery(gallery, target);
  }

  function scanGalleries() {
    all('[data-lande-gallery]').forEach((gallery) => {
      if (!galleriesSeen.has(gallery)) {
        galleriesSeen.add(gallery);
        const track = trackOf(gallery);
        // Certains navigateurs restaurent une position de défilement : on
        // repart de la première photo, mais uniquement à la première liaison.
        if (track) track.scrollLeft = 0;
      }
      syncGallery(gallery);
    });
  }

  /* --- En-tête escamotable et barre d'achat ------------------------------
   * Un seul contrôleur : les deux se disputent le haut de l'écran. Tant que le
   * bouton d'achat est visible, l'en-tête mène ; une fois sorti par le haut, la
   * barre d'achat prend sa place — le haut n'est jamais vide. La position du
   * bouton vient d'un IntersectionObserver : pas de mesure pendant le scroll.
   * --------------------------------------------------------------------- */

  let lastScrollY = 0;
  let scrollingDown = false;
  let ctaPassed = false;
  let ctaObserver = null;
  let observedCta = null;
  let frameQueued = false;
  let dernierCadre = 0;

  /* Le document défile normalement dans Horizon, mais certaines mises en page
     déportent le défilement dans .page-wrapper : on retient la source active. */
  function scrollTopActuel() {
    const y = window.scrollY || document.documentElement.scrollTop || 0;
    if (y > 0) return y;
    const conteneur = document.querySelector('.page-wrapper');
    return conteneur ? conteneur.scrollTop : 0;
  }

  function applyHeaderState() {
    frameQueued = false;
    const header = el('[data-lande-header]');
    const buybar = el('[data-lande-buybar]');
    if (!header && !buybar) return;
    const y = scrollTopActuel();
    const delta = y - lastScrollY;
    if (Math.abs(delta) > HEADER_NOISE_PX) {
      scrollingDown = delta > 0;
      lastScrollY = y;
    }
    /* L'IntersectionObserver n'est pas évalué dans un onglet non peint (aperçu
       en arrière-plan, onglet masqué). On confirme donc l'état par une mesure
       directe : un seul rect par frame déjà throttlée, coût négligeable. */
    if (observedCta && observedCta.isConnected) {
      ctaPassed = observedCta.getBoundingClientRect().bottom <= 0;
    }

    /* En-tête superposé au hero (page d'accueil, façon Louis Vuitton) : il reste
       transparent tant qu'on est en haut, puis se détache — fond opaque, texte
       sombre — et s'escamote alors comme un en-tête collant ordinaire. */
    if (header && header.classList.contains('lande-entete--over')) {
      header.setAttribute('data-lande-detache', String(y > HEADER_GUARD_PX));
    }

    if (ctaPassed) {
      if (buybar) buybar.setAttribute('data-visible', 'true');
      if (header) header.setAttribute('data-hidden', 'false');
      return;
    }
    if (buybar) buybar.setAttribute('data-visible', 'false');
    if (header) header.setAttribute('data-hidden', 'false');
  }

  function requestHeaderFrame() {
    if (frameQueued) return;
    frameQueued = true;
    requestAnimationFrame(applyHeaderState);
  }

  function scanHeader() {
    const cta = el('[data-lande-cta]');
    if (cta !== observedCta) {
      if (ctaObserver) ctaObserver.disconnect();
      ctaObserver = null;
      observedCta = cta;
      ctaPassed = false;
      if (cta) {
        ctaObserver = new IntersectionObserver(
          (entries) => {
            const entry = entries[entries.length - 1];
            // Hors écran ET au-dessus : le bouton d'achat est dépassé.
            ctaPassed = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
            requestHeaderFrame();
          },
          { threshold: 0 }
        );
        ctaObserver.observe(cta);
      }
    }
    lastScrollY = scrollTopActuel();
    applyHeaderState();
  }

  /* --- Dépliage « Lire la suite » ---------------------------------------- */

  function toggleExpandable(button) {
    const id = button.getAttribute('data-lande-expands');
    const target = id ? document.getElementById(id) : button.closest('[data-lande-expandable]');
    if (!target) return;
    const open = target.getAttribute('data-open') !== 'true';
    target.setAttribute('data-open', String(open));
    button.setAttribute('aria-expanded', String(open));
    const label = open ? button.getAttribute('data-label-less') : button.getAttribute('data-label-more');
    if (label && button.textContent !== label) button.textContent = label;
  }

  /* --- Écouteurs globaux, posés une seule fois ---------------------------- */

  function onClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const opener = target.closest('[data-lande-panel-open]');
    if (opener) {
      const name = opener.getAttribute('data-lande-panel-open');
      // Pas de panneau correspondant sur cette page : on laisse le lien vivre.
      if (!panelByName(name)) return;
      event.preventDefault();
      openPanel(name);
      return;
    }

    const closer = target.closest('[data-lande-panel-close]');
    if (closer) {
      event.preventDefault();
      const named = closer.getAttribute('data-lande-panel-close');
      closePanel(named ? panelByName(named) : closer.closest('[data-lande-panel]') || activePanel);
      return;
    }

    if (target.closest('[data-lande-overlay]')) {
      closePanel(activePanel);
      return;
    }

    const goto = target.closest('[data-lande-goto]');
    if (goto) {
      const panel = goto.closest('[data-lande-panel]');
      if (panel) showView(panel, goto.getAttribute('data-lande-goto'));
      return;
    }

    const back = target.closest('[data-lande-back]');
    if (back) {
      const panel = back.closest('[data-lande-panel]');
      if (panel) showView(panel, 'root');
      return;
    }

    const arrow = target.closest('[data-lande-gallery-prev], [data-lande-gallery-next]');
    if (arrow) {
      const gallery = arrow.closest('[data-lande-gallery]');
      const track = gallery ? trackOf(gallery) : null;
      if (gallery && track) {
        event.preventDefault();
        const step = arrow.matches('[data-lande-gallery-next]') ? 1 : -1;
        goToSlide(gallery, shownIndex(gallery, track) + step);
      }
      return;
    }

    const expander = target.closest('[data-lande-expands]');
    if (expander) {
      event.preventDefault();
      toggleExpandable(expander);
    }
  }

  function onKeydown(event) {
    if (event.key === 'Escape' && activePanel) {
      // Dans un panneau à deux niveaux, Échap remonte d'abord au sommaire.
      const view = currentView(activePanel);
      if (view && view.getAttribute('data-lande-view') !== 'root') {
        showView(activePanel, 'root');
        const back = el('[data-lande-back]', activePanel) || el('[data-lande-panel-close]', activePanel);
        if (back) back.focus({ preventScroll: true });
        return;
      }
      closePanel(activePanel);
      return;
    }

    if (event.key === 'Tab') {
      trapTab(event);
      return;
    }

    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    const focused = document.activeElement instanceof Element ? document.activeElement : null;
    const gallery = focused ? focused.closest('[data-lande-gallery]') : null;
    const track = gallery ? trackOf(gallery) : null;
    if (!track || slideCount(track) < 2) return;
    event.preventDefault();
    goToSlide(gallery, shownIndex(gallery, track) + (event.key === 'ArrowRight' ? 1 : -1));
  }

  // `scroll` ne remonte pas, mais il se capture : un écouteur unique en phase
  // de capture suffit pour resynchroniser n'importe quelle piste de galerie.
  let trackFrame = false;
  function onScrollCapture(event) {
    /* Horizon fait défiler .page-wrapper, pas le document : l'écouteur posé sur
       window ne se déclenche jamais. Cette capture est la seule qui voie ce
       défilement — l'en-tête et la barre d'achat en dépendent.
       Mise à jour directe et non via requestAnimationFrame : rAF est suspendu
       dans un onglet non peint (aperçu en arrière-plan), l'en-tête restait
       alors figé. Throttle temporel à 16 ms, coût équivalent. */
    const maintenant = Date.now();
    if (maintenant - dernierCadre > 16) {
      dernierCadre = maintenant;
      applyHeaderState();
    }

    const target = event.target instanceof Element ? event.target : null;
    if (!target || !target.matches('[data-lande-gallery-track]')) return;
    if (trackFrame) return;
    trackFrame = true;
    requestAnimationFrame(() => {
      trackFrame = false;
      const gallery = target.closest('[data-lande-gallery]');
      if (gallery) syncGallery(gallery);
    });
  }

  function onResize() {
    all('[data-lande-gallery]').forEach(syncGallery);
    requestHeaderFrame();
  }

  function reflectMotionPreference() {
    document.documentElement.setAttribute('data-lande-motion', reducedMotion() ? 'reduced' : 'full');
  }

  /* --- Point d'entrée ----------------------------------------------------- */

  let scanning = false;
  function init() {
    if (scanning) return;
    scanning = true;
    try {
      reflectMotionPreference();
      scanPanels();
      scanGalleries();
      scanHeader();
    } finally {
      scanning = false;
    }
  }

  function boot() {
    document.addEventListener('click', onClick);
    document.addEventListener('keydown', onKeydown);
    document.addEventListener('scroll', onScrollCapture, { capture: true, passive: true });
    window.addEventListener('scroll', requestHeaderFrame, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });

    // Retour arrière depuis le cache navigateur : l'état DOM peut être figé.
    window.addEventListener('pageshow', init);
    // Aperçu de l'éditeur de thème : une section rechargée est un DOM neuf.
    document.addEventListener('shopify:section:load', init);

    if (typeof motionQuery.addEventListener === 'function') {
      motionQuery.addEventListener('change', reflectMotionPreference);
    }

    // Filet pour les re-rendus de section d'Horizon (facettes, variantes,
    // hydratation) : on rescanne au rythme de l'affichage, jamais par mutation.
    if (typeof MutationObserver === 'function') {
      let pending = false;
      const observer = new MutationObserver(() => {
        if (scanning || pending) return;
        pending = true;
        requestAnimationFrame(() => {
          pending = false;
          init();
        });
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }

    init();
  }

  window.Lande = Object.freeze({ init: init, version: '1.0.0' });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
